import { queryOptions } from '@tanstack/react-query';
import { api, apiList } from '@/lib/api-client';
import type {
  AutomationListItem,
  ChecklistTemplate,
  ChecklistTemplateListItem,
  Client,
  DeskCloseReason,
  DeskDetail,
  DeskListItem,
  Holiday,
  ScheduledActivityListItem,
  ScheduleSlot,
} from '@erp/shared';
import { deskCatalogsQuery } from '@/features/tickets/tickets-queries';
import { errorMessage as genericErrorMessage } from '@/features/tickets/ticket-format';

export function desksListOptions() {
  return queryOptions({
    queryKey: ['erp', 'desks', 'list'] as const,
    queryFn: async () => {
      // spec 017: o item traz as etapas "a revisar" (a lista de mesas as mostra)
      const res = await apiList<DeskListItem>('/desks', { query: { limit: 200 } });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function deskDetailOptions(deskId: number) {
  return queryOptions({
    queryKey: ['erp', 'desks', 'detail', deskId] as const,
    queryFn: async () => {
      const res = await api<DeskDetail>(`/desks/${deskId}`);
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** Os motivos de fechamento da mesa (spec 054), ativos e inativos, na ordem. */
export function deskCloseReasonsOptions(deskId: number) {
  return queryOptions({
    queryKey: ['erp', 'desks', 'close-reasons', deskId] as const,
    queryFn: async ({ signal }) => {
      const res = await api<DeskCloseReason[]>(`/desks/${deskId}/close-reasons`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

// ── Expediente + feriados + catálogos + automações (v3) ─────────────────────

export function deskScheduleOptions(deskId: number) {
  return queryOptions({
    queryKey: ['erp', 'desks', 'schedule', deskId] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ScheduleSlot[]>(`/desks/${deskId}/schedule`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function holidaysOptions() {
  return queryOptions({
    queryKey: ['erp', 'desks', 'holidays'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<Holiday[]>('/desks/holidays', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** Catálogos vinculados à mesa, normalizados para ids (reusa a query de tickets). */
export function deskCatalogIdsOptions(deskId: number) {
  return queryOptions({
    ...deskCatalogsQuery(deskId),
    select: (catalogs) => catalogs.map((c) => c.id),
  });
}

/** Templates de checklist da mesa (v4) — reusado pelo detalhe do ticket. */
export function deskChecklistTemplatesOptions(deskId: number) {
  return queryOptions({
    queryKey: ['erp', 'checklists', 'templates', { deskId }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<ChecklistTemplate>('/checklists', {
        query: { desk_id: deskId },
        signal,
      });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}

// ── Listas cross-mesa (telas de configuração de Mesa de serviço) ────────────
// Contrato v8: deskName vem do join na API (= displayName); em automação,
// deskId/deskName nulos = automação global ('Todas' as mesas). As listas já
// chegam ORDENADAS do servidor (mesa asc, 'Todas' no fim) — as telas não
// re-sorteiam.

export type { AutomationListItem, ChecklistTemplateListItem, ScheduledActivityListItem };

/** GET /automations — todas as mesas + globais (deskId/deskName nulos). */
export function allAutomationsOptions() {
  return queryOptions({
    queryKey: ['erp', 'automations', 'all'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<AutomationListItem[]>('/automations', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
  });
}

/** GET /scheduled-activities — atividades agendadas de todas as mesas. */
export function allScheduledOptions() {
  return queryOptions({
    queryKey: ['erp', 'scheduled', 'all'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ScheduledActivityListItem[]>('/scheduled-activities', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
  });
}

/** GET /checklists (sem desk_id) — templates de todas as mesas. */
export function allChecklistTemplatesOptions() {
  return queryOptions({
    queryKey: ['erp', 'checklists', 'templates', 'all'] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<ChecklistTemplateListItem>('/checklists', { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
    staleTime: 60_000,
  });
}

// ── Lookups de outros domínios (mesma convenção de chave = cache partilhado) ─

export function clientsLookupOptions() {
  return queryOptions({
    queryKey: ['erp', 'clients', 'list', { limit: 200 }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<Client>('/clients', { query: { limit: 200 }, signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
    staleTime: 60_000,
  });
}

/**
 * Códigos de erro da API com mensagem amigável em pt-BR — helper partilhado
 * pelas features de configurações (mesas, entidades, grupos de permissão).
 */
const ERROR_LABELS: Record<string, string> = {
  catalog_in_use: 'Em uso por tickets — não é possível excluir.',
  holiday_date_taken: 'Já existe um feriado cadastrado nessa data.',
  invalid_schedule_slot: 'Faixa de horário inválida: o início deve ser antes do fim.',
  permission_group_not_found:
    'Grupo de permissão não encontrado — a lista pode estar desatualizada.',
  permission_group_name_taken: 'Já existe um grupo de permissão com esse nome.',
  forbidden: 'Você não tem permissão para executar esta ação.',
  validation_error: 'Dados inválidos — revise os campos e tente de novo.',
};

/** Códigos locais primeiro; os transversais (permission_denied…) vêm do
 *  fallback genérico do ticket-format — fonte única do wording. */
export function errorMessage(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    const label = ERROR_LABELS[(err as { error: string }).error];
    if (label) return label;
  }
  return genericErrorMessage(err);
}
